import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { CircleLoader } from "../../shared/components/circle-loader/CircleLoader";
import { isValidCampaignCardId } from "../../shared/utils/normalize-campaign-card";
import "./campaign-detail.scss";
import { AnalyticsSection } from "./components/AnalyticsSection";
import { CampaignBanners } from "./components/CampaignBanners";
import { CampaignDetailError } from "./components/CampaignDetailError";
import { CampaignDetailHeader } from "./components/CampaignDetailHeader";
import { CampaignKpiSection } from "./components/CampaignKpiSection";
import { HourlySection } from "./components/HourlySection";
import { StructureSection } from "./components/StructureSection";
import { CampaignCardFetchError } from "./errors/campaign-card-fetch-error";
import {
	campaignCardQueryKey,
	useCampaignCard,
} from "./service/useCampaignCard";

function errorVariant(
	err: unknown,
): "forbidden" | "not-found" | "generic" {
	if (err instanceof CampaignCardFetchError && err.status === 404) {
		return "not-found";
	}
	if (err instanceof CampaignCardFetchError) {
		return "forbidden";
	}
	return "generic";
}

export const CampaignDetailPage = () => {
	const { campaignId } = useParams<{ campaignId: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const query = useCampaignCard(campaignId);

	const handleRefresh = () => {
		if (campaignId) {
			void queryClient.invalidateQueries({
				queryKey: campaignCardQueryKey(campaignId),
			});
		}
	};

	if (!campaignId || !isValidCampaignCardId(campaignId)) {
		return (
			<div className='campaign-detail'>
				<CampaignDetailError
					variant='invalid-id'
					onHome={() => navigate("/main")}
				/>
			</div>
		);
	}

	if (query.isPending || (query.isFetching && !query.data)) {
		return (
			<div className='campaign-detail'>
				<div className='campaign-detail__loader-wrap'>
					<CircleLoader />
				</div>
			</div>
		);
	}

	if (query.isError || !query.data) {
		return (
			<div className='campaign-detail'>
				<CampaignDetailError
					variant={errorVariant(query.error)}
					onRetry={() => void query.refetch()}
					onHome={() => navigate("/main")}
				/>
			</div>
		);
	}

	const campaign = query.data;

	return (
		<div className='campaign-detail'>
			<CampaignDetailHeader
				campaign={campaign}
				onRefresh={handleRefresh}
				isRefreshing={query.isFetching}
			/>
			<main className='campaign-detail__main'>
				<CampaignBanners campaign={campaign} />
				<CampaignKpiSection campaign={campaign} />
				<HourlySection campaign={campaign} />
				<AnalyticsSection campaign={campaign} />
				<StructureSection campaign={campaign} />
			</main>
		</div>
	);
};
